import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { CandidateFormItemFormType } from '@/types/serializers';

import { Button } from '@/components/Button';
import { selectableFormTypeOptions } from '@/features/agent/form/candidateFormItemOptions';
import useUpdateCandidateFormItem from '@/features/agent/form/api/useUpdateCandidateFormItem';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 現在のフォーム種別（selectbox | radio | checkbox） */
  currentFormType: CandidateFormItemFormType;
  candidateFormId: number;
  candidateFormItemId: number;
  /** 保存成功時に呼ばれる */
  onSaved?: () => void;
};

export default function ChangeInfoTypeDialog({
  open,
  onClose,
  currentFormType,
  candidateFormId,
  candidateFormItemId,
  onSaved,
}: Props) {
  const [selectedFormType, setSelectedFormType] =
    useState<CandidateFormItemFormType>(currentFormType);

  const { mutateAsync: updateItem, isPending: isUpdating } = useUpdateCandidateFormItem();

  useEffect(() => {
    if (open) {
      setSelectedFormType(currentFormType);
    }
  }, [open, currentFormType]);

  const handleSave = async () => {
    if (selectedFormType === currentFormType) {
      onClose();
      return;
    }
    try {
      await updateItem({
        candidateFormId,
        candidateFormItemId,
        form_type: selectedFormType,
      });
      showSuccessToast('情報タイプを変更しました');
      onClose();
      onSaved?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '情報タイプの変更に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="w-full max-w-md rounded-sm shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]!">
        <div className="border-b border-solid border-border px-lg py-md">
          <DialogTitle className="text-base! leading-normal font-bold! text-main">
            情報タイプの変更
          </DialogTitle>
        </div>
        <div className="space-y-4 px-lg py-md">
          <fieldset className="space-y-2">
            <legend className="sr-only">情報タイプを選択</legend>
            {selectableFormTypeOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-3 rounded-sm py-2 hover:bg-gray-50"
              >
                <input
                  type="radio"
                  name="formType"
                  value={opt.value}
                  checked={selectedFormType === opt.value}
                  onChange={() => setSelectedFormType(opt.value)}
                  className="size-4 border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-main">{opt.label}</span>
              </label>
            ))}
          </fieldset>
        </div>
        <div className="flex justify-end gap-md border-t border-solid border-border px-lg py-md">
          <Button type="button" color="secondary" size="md" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            type="button"
            color="primary"
            size="md"
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? '保存中...' : '保存する'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
